"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useClientSidebar } from "@/components/sidebar/client-sidebar-context";

const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true as const,
};

/**
 * Demo Mode switch, moved out of the sidebar footer into the top bar
 * (2026-08-01). Staff only. While demo mode is on it grows a Guided Tours
 * shortcut beside it, which is where the tours are launched from.
 *
 * During an active tour the overlay's click shield covers the chrome; the
 * tooltip card's End-tour link and Escape are the exits.
 */
export function DemoToggle({ variant = "topbar" }: { variant?: "topbar" | "drawer" }) {
  const { demoMode, setDemoMode, setMobileNavOpen } = useClientSidebar();
  const router = useRouter();
  const pathname = usePathname();
  const drawer = variant === "drawer";

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        data-tour="demo-toggle"
        onClick={() => {
          const turningOff = demoMode;
          setDemoMode(!demoMode);
          // The demo companies do not exist outside demo mode, so leaving it
          // while parked on one has to go home rather than 404.
          if (turningOff && /\/clients\/demo-\d+/.test(pathname)) {
            router.push("/");
          } else {
            router.refresh();
          }
        }}
        title={demoMode ? "Disable demo mode" : "Enable demo mode"}
        aria-pressed={demoMode}
        className={`flex items-center gap-1.5 rounded-md font-medium transition-colors ${
          drawer ? "min-h-11 w-full px-3 text-sm" : "h-8 px-2 text-xs"
        } ${
          demoMode
            ? "bg-spark-yellow-tint text-spark-yellow-text hover:bg-spark-yellow/[.25]"
            : "text-spark-navigation-text hover:bg-spark-navigation-hover hover:text-spark-navigation-strong"
        }`}
      >
        <svg {...svgProps}>
          {demoMode ? (
            <>
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </>
          ) : (
            <>
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </>
          )}
        </svg>
        {demoMode ? "Demo On" : "Demo"}
      </button>

      {demoMode && (
        <Link
          href="/agency/tours"
          onClick={() => setMobileNavOpen(false)}
          title="Guided tours"
          aria-label="Guided tours"
          className={`flex items-center justify-center rounded-md text-spark-yellow-text transition-colors hover:bg-spark-yellow/[.15] ${
            drawer ? "h-11 w-11" : "h-8 w-8"
          }`}
        >
          <svg {...svgProps}>
            <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
            <line x1="9" y1="3" x2="9" y2="18" />
            <line x1="15" y1="6" x2="15" y2="21" />
          </svg>
        </Link>
      )}
    </div>
  );
}
